import type { MergedUsage } from "@cmmd-forge/shared/usageMerge";
import type { EnvironmentTotals } from "@cmmd-forge/shared/usageMerge";
import type { UsageProviderKind } from "@cmmd-forge/contracts";
import type { makeWindow } from "@cmmd-forge/shared/usageFormat";

import type { UsageSourceStatus } from "../../usage/useUsage";
import { UsageBreakdown, type UsageBreakdownMode } from "./UsageBreakdown";
import { UsageCoverageNotice } from "./UsageCoverage";
import { UsageLanes } from "./UsageLanes";
import { UsageProviderChart, type UsageChartMetric } from "./UsageProviderChart";
import { UsageSummaryPanel, UsageTotals } from "./UsageSummaryPanel";

interface UsageChartSectionProps {
  readonly providers: Parameters<typeof UsageProviderChart>[0]["providers"];
  readonly days: readonly string[];
  readonly daily: Parameters<typeof UsageProviderChart>[0]["daily"];
  readonly hours: readonly string[];
  readonly hourly: Parameters<typeof UsageProviderChart>[0]["hourly"];
  readonly isPast24Hours: boolean;
  readonly metric: UsageChartMetric;
  readonly referenceTime: string | undefined;
  readonly resolution: "day" | "hour";
  readonly timeZone: string;
}

/**
 * Chart heading and the chart itself.
 *
 * No legend: the summary column beside it already carries a colour dot per
 * lane, and a second key repeating the same three names is noise.
 */
function UsageChartSection({
  providers,
  days,
  daily,
  hours,
  hourly,
  isPast24Hours,
  metric,
  referenceTime,
  resolution,
  timeZone,
}: UsageChartSectionProps) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h2 className="text-sm font-medium text-foreground">
        {isPast24Hours ? "Hourly" : "Daily"} {metric === "tokens" ? "processed tokens" : "cost"}
      </h2>
      <UsageProviderChart
        providers={providers}
        days={days}
        daily={daily}
        hours={hours}
        hourly={hourly}
        metric={metric}
        referenceTime={referenceTime}
        resolution={resolution}
        timeZone={timeZone}
      />
    </div>
  );
}

/**
 * Everything the tab draws once every source is terminal: coverage, the cost
 * headline and lane split, the chart, totals, where the numbers come from,
 * and the breakdown. Split from the tab so the tab owns only the window and
 * the toggles.
 */
export function UsageEstimateLoaded({
  merged,
  sources,
  labelForEnvironment,
  metric,
  breakdown,
  onBreakdownChange,
  activeProviders,
  days,
  hours,
  breakdownPeriods,
  isPast24Hours,
  resolution,
  window,
}: {
  readonly merged: MergedUsage;
  readonly sources: readonly UsageSourceStatus[];
  readonly labelForEnvironment: (row: EnvironmentTotals) => string;
  readonly metric: UsageChartMetric;
  readonly breakdown: UsageBreakdownMode;
  readonly onBreakdownChange: (mode: UsageBreakdownMode) => void;
  readonly activeProviders: readonly UsageProviderKind[];
  readonly days: readonly string[];
  readonly hours: readonly string[];
  readonly breakdownPeriods: MergedUsage["daily"] | MergedUsage["hourly"];
  readonly isPast24Hours: boolean;
  readonly resolution: "day" | "hour";
  readonly window: ReturnType<typeof makeWindow>;
}) {
  return (
    <>
      <UsageCoverageNotice
        sources={sources}
        duplicateSources={merged.duplicateSources}
        staleSources={merged.staleSources}
        claimedRuntimeIds={merged.claimedRuntimeIds}
      />

      {/* Cost first: the financial answer, then the per-lane split. */}
      <section className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <UsageSummaryPanel
          metric={metric}
          costUsd={merged.costUsd}
          totalTokens={merged.totalTokens}
          sessions={merged.sessions}
          providers={merged.providers}
          activeProviders={activeProviders}
        />

        <UsageChartSection
          providers={activeProviders}
          days={days}
          daily={merged.daily}
          hours={hours}
          hourly={merged.hourly}
          isPast24Hours={isPast24Hours}
          metric={metric}
          referenceTime={window.untilTime}
          resolution={resolution}
          timeZone={window.timeZone}
        />
      </section>

      <UsageTotals merged={merged} />

      <UsageLanes
        pendingSync={merged.pendingSync}
        localOnly={merged.localOnly}
        forgeAgents={merged.forgeAgents}
        costProvenance={merged.costProvenance}
        costQuality={merged.costQuality}
      />

      <UsageBreakdown
        mode={breakdown}
        onModeChange={onBreakdownChange}
        metric={metric}
        environments={merged.environments}
        labelForEnvironment={labelForEnvironment}
        providers={merged.providers}
        models={merged.models}
        drivers={merged.drivers}
        kinds={merged.kinds}
        periods={breakdownPeriods}
        activeProviders={activeProviders}
        resolution={resolution}
        timeZone={window.timeZone}
      />
    </>
  );
}
